// Copyright (c) Docugami, Inc. All rights reserved.

import { CanvasHelperStatics } from "components/doc_hosting/doc_hosts/web/CanvasHelperStatics";
import { ChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { ImageComponent } from "sdv/components/ImageComponent";
import { OutflowRenderComponent } from "sdv/components/OutflowRenderComponent";
import { OutlineComponent } from "sdv/components/OutlineComponent";
import { SvgComponent, SvgPath } from "sdv/components/SvgComponent";
import { TableChunkRenderComponent } from "sdv/components/TableChunkRenderComponent";
import { ComponentId } from "sdv/engine/Component";
import { growBox } from "store/shared_types";
import { IDrawCommand, IRenderer, IRendererInput, Queries } from "../engine/Engine";

/**
 * Renders canvas chunk controls - small, large, and table chunks
 */
export class ControlRenderer implements IRenderer {
    get renderEnabled(): boolean { return true; }

    queries: Queries = {
        chunks: { types: [ComponentId.ChunkRender, ComponentId.Renderable] },
        svgRenderables: { types: [ComponentId.Svg, ComponentId.Renderable] },
        imageRenderables: { types: [ComponentId.Image, ComponentId.Renderable] },
        tableChunks: { types: [ComponentId.TableChunkRender] },
        outflows: { types: [ComponentId.OutflowRender, ComponentId.Renderable] },
        outlineTables: { types: [ComponentId.TableChunkRender, ComponentId.Outline] }
    }

    render(engine: IRendererInput): IDrawCommand[] {
        const commands: IDrawCommand[] = []
        for (const chunk of this.queries.chunks.query!.entities) {
            const renderInfo = chunk.getComponent<ChunkRenderComponent>(ComponentId.ChunkRender)!;
            commands.push(context => drawChunkControl(context, renderInfo));
        }

        for (const [svg] of this.queries.svgRenderables.query!.entities.map((e): [SvgComponent] => [e.getComponent<SvgComponent>(ComponentId.Svg)!])) {
            svg.paths.forEach(path => commands.push(context => drawSvgPath(context, svg, path)));
        }

        for (const [image] of this.queries.imageRenderables.query!.entities.map((e): [ImageComponent] => [e.getComponent<ImageComponent>(ComponentId.Image)!])) {
            commands.push(context => drawImage(context, image));
        }

        for (const tableChunk of this.queries.tableChunks.query!.entities) {
            const renderChunk = tableChunk.hasComponent(ComponentId.Renderable);
            const outlineChunk = tableChunk.hasComponent(ComponentId.Outline);
            if (renderChunk || outlineChunk) {
                const renderInfo = tableChunk.getComponent<TableChunkRenderComponent>(ComponentId.TableChunkRender)!;
                const outlineColor = tableChunk.getComponent<OutlineComponent>(ComponentId.Outline)?.color ?? '';
                commands.push(context => drawTableChunkControl(context, renderInfo, renderChunk, outlineColor, tableChunk.hasComponent(ComponentId.TableColumnRender)));
            }
        }

        for (const outflow of this.queries.outflows.query!.entities) {
            const renderInfo = outflow.getComponent<OutflowRenderComponent>(ComponentId.OutflowRender)!;
            commands.push(context => drawOutflowControl(context, renderInfo));
        }

        return commands;
    }
}

function drawChunkControl(context: CanvasRenderingContext2D, chunk: ChunkRenderComponent): void {
    for (const box of chunk.renderBoxes) {
        if (!!box.color) {
            CanvasHelperStatics.fillRectangle(
                context,
                box.box,
                box.color,
                true);
        }
    }
}

function drawTableChunkControl(context: CanvasRenderingContext2D, tableChunk: TableChunkRenderComponent, renderChunk: boolean = true, outlineColor: string = '', asColumn: boolean = false): void {
    const boxes = (asColumn ? tableChunk.columnBoxes : tableChunk.boxes) ?? [];
    for (const box of boxes) {
        if (!!outlineColor) {
            CanvasHelperStatics.strokeRectangle(
                context,
                growBox(box.box, 1),
                outlineColor,
                2,
                false);
        }

        if (renderChunk && !!box.color) {
            CanvasHelperStatics.fillRectangle(
                context,
                box.box,
                box.color,
                true);
        }
    }
}

function drawOutflowControl(context: CanvasRenderingContext2D, chunk: OutflowRenderComponent): void {
    for (const box of chunk.renderBoxes) {
        if (!!box.color) {
            CanvasHelperStatics.fillRectangle(
                context,
                box.box,
                box.color,
                true);
        }
    }
}

function drawSvgPath(context: CanvasRenderingContext2D, svg: SvgComponent, path: SvgPath) {
    CanvasHelperStatics.drawPathNew(
        context,
        svg.point.x,
        svg.point.y,
        path.path,
        path.color,
        svg.scale);
}

function drawImage(context: CanvasRenderingContext2D, image: ImageComponent) {
    if (!!image.loaded) {
        context.drawImage(
            image.image,
            image.position.x,
            image.position.y
        )
    }
}
